import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * A finished Hide and Seek round's pathways: the route each player wearing
 * Krall's Accord took, drawn on the round's planet map with the same zone
 * drill-down as the Marco Polo map, beside the list of players.
 *
 * - Pick a player to bring their path forward (the rest fade); untick one to
 *   hide them. Players who didn't wear it are listed as such.
 * - The time slider replays the round: every path up to that moment, each
 *   player at where they were. Play runs it at 20× speed.
 * - Clicking where a player ended opens the zone it is in.
 *
 * Shared by admin-web and the Electron client, over `buildHnsPathView`. Pure:
 * the caller fetches the paths (once the round has ended) and says how to load
 * an image.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { buildHnsPathView, pathMarkerId, } from '@swtorbit/shared-socket-events';
import { GameMap } from './GameMap';
import { useZoneDrillDown, ZoneDrillControls } from './ZoneDrillDown';
/** Replay speed: round seconds per real second. */
const PLAY_SPEED = 20;
const PLAY_TICK_MS = 100;
function formatClock(ms) {
    const total = Math.max(0, Math.round(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
export function HnsPathwaysMap(props) {
    // A new round starts a new replay: its own zoom, picks and time.
    return _jsx(PathwaysMap, { ...props }, `${props.round.id}:${props.location.logAreaId}`);
}
function PathwaysMap({ round, location, players, paths, viewerId = null, resolveImageUrl = (url) => url }) {
    const drill = useZoneDrillDown(location, { enabled: true, startZoneId: location.zone?.id ?? null, resolveImageUrl });
    const map = location.world ?? location.zone;
    const [hidden, setHidden] = useState(() => new Set());
    const [selectedId, setSelectedId] = useState(null);
    const [until, setUntil] = useState(null);
    const [playing, setPlaying] = useState(false);
    const view = useMemo(() => (map ? buildHnsPathView({ round, location, map, paths, players, viewerId, until, hidden, selectedPlayerId: selectedId }) : null), [round, location, map, paths, players, viewerId, until, hidden, selectedId]);
    const startAt = view?.startAt ?? null;
    const endAt = view?.endAt ?? null;
    // Play: move the moment on until the end, then stop.
    const untilRef = useRef(until);
    useEffect(() => {
        untilRef.current = until;
    }, [until]);
    useEffect(() => {
        if (!playing || startAt === null || endAt === null)
            return;
        const timer = setInterval(() => {
            const next = (untilRef.current ?? startAt) + PLAY_SPEED * PLAY_TICK_MS;
            if (next >= endAt) {
                setPlaying(false);
                setUntil(null);
            }
            else {
                setUntil(next);
            }
        }, PLAY_TICK_MS);
        return () => clearInterval(timer);
    }, [playing, startAt, endAt]);
    if (!map || !view)
        return _jsx("p", { className: "fs-sm text-slate m-0", children: "No map for this round." });
    const withPath = view.rows.filter((row) => row.hasPath);
    const toggle = (playerId) => setHidden((current) => {
        const next = new Set(current);
        if (next.has(playerId))
            next.delete(playerId);
        else
            next.add(playerId);
        return next;
    });
    function selectMarker(markerId) {
        const row = markerId ? view?.rows.find((r) => pathMarkerId(r.playerId) === markerId) : undefined;
        setSelectedId(row?.playerId ?? null);
        if (!row || !markerId)
            return;
        // Open the zone the player ended in.
        const marker = view?.markers.find((m) => m.id === markerId);
        const path = paths.find((p) => p.playerId === row.playerId)?.points.filter((p) => until === null || p.at <= until);
        const last = path?.[path.length - 1];
        const zoneId = last ? drill.zoneAt(last.x, last.y) : null;
        if (zoneId !== null && zoneId !== drill.focusZoneId)
            drill.openZone(zoneId);
        else if (zoneId === null && marker && drill.focusZoneId === null) {
            drill.focusOn({ x: marker.x - 4, y: marker.y - 4, w: 8, h: 8 });
        }
    }
    if (withPath.length === 0) {
        return _jsx("p", { className: "fs-sm text-slate m-0", children: "Nobody wore Krall's Accord this round, so there are no paths to show." });
    }
    return (_jsxs("div", { className: "hns-pathways d-flex flex-column gap-2xs", children: [_jsx(ZoneDrillControls, { drill: drill, location: location }), _jsxs("div", { className: "hns-pathways__body", children: [_jsx(GameMap, { className: "game-map--paths", imageUrl: resolveImageUrl(map.image.url), width: map.image.width, height: map.image.height, name: map.name, markers: view.markers, trails: view.trails, interactive: true, maxZoom: drill.maxZoom, focus: drill.focus, layers: drill.layers, regions: drill.regions, activeRegionId: drill.focusZoneId !== null ? String(drill.focusZoneId) : null, onRegionSelect: (id) => drill.openZone(Number(id)), selectedMarkerId: selectedId ? pathMarkerId(selectedId) : null, onMarkerSelect: selectMarker }), _jsxs("ul", { className: "hns-pathways__players fs-sm", "aria-label": "Players", children: [view.rows.map((row) => (_jsx("li", { className: `hns-pathways__player${row.playerId === selectedId ? ' is-selected' : ''}${row.hasPath ? '' : ' is-empty'}`, children: row.hasPath ? (_jsxs(_Fragment, { children: [_jsx("input", { type: "checkbox", className: "checkbox", checked: !hidden.has(row.playerId), onChange: () => toggle(row.playerId), "aria-label": `Show ${row.name}'s path` }), _jsxs("button", { type: "button", className: "hns-pathways__name", "aria-pressed": row.playerId === selectedId, onClick: () => setSelectedId(row.playerId === selectedId ? null : row.playerId), children: [_jsx("span", { className: `hns-pathways__swatch game-map__marker--${row.tone}`, "aria-hidden": "true" }), row.name, row.playerId === viewerId ? ' (you)' : ''] })] })) : (_jsxs("span", { className: "text-slate", children: [row.name, " \u00B7 no Krall's Accord"] })) }, row.playerId))), withPath.length > 1 && (_jsx("li", { className: "hns-pathways__player", children: _jsx("button", { type: "button", className: "btn btn--xs btn--outline btn--slate", onClick: () => setHidden(hidden.size > 0 ? new Set() : new Set(withPath.map((row) => row.playerId))), children: hidden.size > 0 ? 'Show all' : 'Hide all' }) }))] })] }), startAt !== null && endAt !== null && endAt > startAt && (_jsxs("div", { className: "hns-pathways__scrubber", children: [_jsx("button", { type: "button", className: "btn btn--xs", "aria-label": playing ? 'Pause' : 'Play', onClick: () => {
                            if (!playing && until === null)
                                setUntil(startAt);
                            setPlaying(!playing);
                        }, children: playing ? '⏸' : '▶' }), _jsx("input", { type: "range", className: "range", min: startAt, max: endAt, step: 1000, value: until ?? endAt, "aria-label": "Round time", onChange: (event) => {
                            const moment = Number(event.target.value);
                            setPlaying(false);
                            setUntil(moment >= endAt ? null : moment);
                        } }), _jsxs("span", { className: "fs-xs text-slate", children: [formatClock((until ?? endAt) - startAt), " / ", formatClock(endAt - startAt)] })] }))] }));
}
